import {
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions,
  useInfiniteQuery,
  useQuery,
} from "@tanstack/react-query";
import { useRequiredParams } from "~utils/url";
import { ApiResponse, ApiResponsePaginated } from "~api/api-response.model";
import { useSuspenseQueryDeferred } from "~utils/query-hooks";
import { assetsApiRequest } from "../api/api-request";
import {
  Point,
  PointResponse,
  PointType,
  PointWithBuilding,
  WithValue,
} from "./point.model";
import { Alarm } from "../alarm/alarm.model";
import { defaultPageSize, QueryOptions } from "../utils/consts";
import { DataType } from "./point-energy-reports/point-energy.utils";
import { PointTypeFactory } from "./point-type-factory";

export type PointListParams = {
  companyId: number;
  deviceId?: number;
  levelId?: number;
  search?: string;
  types?: PointType[];
  sort?: string;
  order?: string;
  externalIds?: string[];
  pointNames?: string[];
  excludeType?: PointType[];
  maxResults?: number;
  lockStates?: string[];
  withValues: boolean;
  dataType?: DataType;
};

type PointParam = keyof Omit<Point & Alarm, "id">;
type PointFiltersParams = {
  companyId: number;
  select: "alarmGroup"[];
};
type PointGFXQueryParams = {
  companyId: number;
  externalPoints: string[];
  types?: PointType[];
};
type PointDetailsParams = {
  companyId: number;
  deviceId?: number;
  id?: number;
};
type PointsByIdsParams = {
  companyId: number;
  pointIds: number[];
  withValues: boolean;
  dataType?: DataType;
};

export const pointQueries = {
  infinite: (params: PointListParams) =>
    infiniteQueryOptions({
      queryKey: ["point-list-infinite", params],
      gcTime: 0,
      queryFn: ({ signal, pageParam }) =>
        fetchInfinitePoints({ ...params, pageParam }, signal),
      initialPageParam: 1,
      getNextPageParam: (lastPage, _allPages, lastPageParam) => {
        // If the length of last page in our fetched pages is less
        // than the page size, we know that there is no more new data to be fetched
        if (
          !lastPage ||
          lastPage.points.length < (params.maxResults ?? defaultPageSize)
        ) {
          return undefined;
        }
        return lastPageParam + 1;
      },
    }),
  list: (params: PointListParams, options?: QueryOptions) =>
    queryOptions({
      queryKey: [
        options?.customQueryKey ? options.customQueryKey : "point-list",
        params,
      ],
      queryFn: ({ signal }) => fetchPoints(params, signal),
      ...options,
    }),
  gfx: (params: PointGFXQueryParams, options?: QueryOptions) =>
    queryOptions({
      queryKey: ["point-gfx", params],
      queryFn: ({ signal }) => fetchGFXPoints(params, signal),
      ...options,
    }),
  byIds: (params: PointsByIdsParams, options?: QueryOptions) =>
    queryOptions({
      queryKey: [
        options?.customQueryKey ? options.customQueryKey : "points-by-ids",
        params,
      ],
      queryFn: ({ signal }) => fetchPointsByIds(params, signal),
      placeholderData: keepPreviousData,
      ...options,
    }),
  details: (params: PointDetailsParams, options?: QueryOptions) =>
    queryOptions({
      queryKey: [
        options?.customQueryKey ? options.customQueryKey : "point-details",
        params,
      ],
      queryFn: ({ signal }) => fetchPoint(params, signal),
      ...options,
    }),
  filters: (params: PointFiltersParams) =>
    queryOptions({
      queryKey: ["point-filters", params],
      queryFn: ({ signal }) => fetchFilters(params, signal),
    }),
  calendarFeed: (params: PointDetailsParams, options?: QueryOptions) =>
    queryOptions({
      queryKey: [
        options?.customQueryKey
          ? options.customQueryKey
          : "point-calendar-feed",
        params,
      ],
      queryFn: ({ signal }) => fetchCalendarPointFeed(params, signal),
      ...options,
    }),
};

// Queries

export function usePointsInfiniteQuery(
  params: Omit<PointListParams, "companyId">,
) {
  const { companyId } = useRequiredParams({ companyId: "number" });
  return useInfiniteQuery(pointQueries.infinite({ companyId, ...params }));
}
export function usePointsGFXQuery(
  params: Omit<PointGFXQueryParams, "companyId">,
  options?: QueryOptions,
) {
  const { companyId } = useRequiredParams({ companyId: "number" });
  return useQuery(pointQueries.gfx({ companyId, ...params }, options));
}

export function usePointsSuspenseQuery(
  params: Omit<PointListParams, "companyId">,
  options?: QueryOptions,
) {
  const { companyId } = useRequiredParams({ companyId: "number" });
  return useSuspenseQueryDeferred(
    pointQueries.list({ companyId, ...params }, options),
  );
}

export function usePointFiltersQuery(
  params: Omit<PointFiltersParams, "companyId">,
) {
  const { companyId } = useRequiredParams({ companyId: "number" });
  return useQuery(pointQueries.filters({ companyId, ...params }));
}

export function usePointsQuery(
  params: Omit<PointListParams, "companyId">,
  options?: QueryOptions,
) {
  const { companyId } = useRequiredParams({ companyId: "number" });
  return useQuery(pointQueries.list({ companyId, ...params }, options));
}

export function usePointsByIdsSuspenseQuery(
  params: Omit<PointsByIdsParams, "companyId">,
  options?: QueryOptions,
) {
  const { companyId } = useRequiredParams({ companyId: "number" });
  return useSuspenseQueryDeferred(
    pointQueries.byIds({ companyId, ...params }, options),
  );
}

export function usePointsByIdsQuery(
  params: Omit<PointsByIdsParams, "companyId">,
  options?: QueryOptions,
) {
  const { companyId } = useRequiredParams({ companyId: "number" });
  return useQuery(pointQueries.byIds({ companyId, ...params }, options));
}

export function usePointSuspenseQuery(
  params: Omit<PointDetailsParams, "companyId">,
  options?: QueryOptions,
) {
  const { companyId } = useRequiredParams({ companyId: "number" });
  return useSuspenseQueryDeferred(
    pointQueries.details({ companyId, ...params }, options),
  );
}

export function usePointQuery(
  params: Omit<PointDetailsParams, "companyId">,
  options?: QueryOptions,
) {
  const { companyId } = useRequiredParams({ companyId: "number" });
  return useQuery(pointQueries.details({ companyId, ...params }, options));
}

export function useCalendarPointFeedQuery(
  params: Omit<PointDetailsParams, "companyId">,
  options?: QueryOptions,
) {
  const { companyId } = useRequiredParams({ companyId: "number" });
  return useQuery(pointQueries.calendarFeed({ companyId, ...params }, options));
}

// Fetchers

export async function fetchInfinitePoints(
  { companyId, pageParam, ...params }: PointListParams & { pageParam: number },
  signal?: AbortSignal,
) {
  return assetsApiRequest
    .get<ApiResponsePaginated<(Point & PointWithBuilding)[]>>(
      `analytics/companies/${companyId}/points`,
      {
        // Get points from nested levels if levelId is provided
        params: {
          ...params,
          page: pageParam,
          nested: params.levelId ? true : undefined,
        },
        signal,
      },
    )
    .then((resp) => ({
      points: resp.data.data,
      pagination: resp.data.meta.pagination ?? {
        page: pageParam,
        pageSize: params.maxResults,
      },
    }));
}

export async function fetchPoints(
  { companyId, ...params }: PointListParams,
  signal?: AbortSignal,
) {
  return assetsApiRequest
    .get<ApiResponse<(Point & PointWithBuilding & WithValue)[]>>(
      `analytics/companies/${companyId}/points`,
      {
        // Get points from nested levels if levelId is provided
        params: {
          ...params,
          nested: params.levelId ? true : undefined,
        },
        signal,
      },
    )
    .then((resp) => resp.data.data);
}

export async function fetchPointsByIds(
  { companyId, ...params }: PointsByIdsParams,
  signal?: AbortSignal,
) {
  // For suspense queries we need to return an empty response
  if (params.pointIds.length === 0) {
    return {
      points: [],
      pagination: { total: 0, totalPages: 0, page: 1, pageSize: 0 },
    };
  }

  return assetsApiRequest
    .get<ApiResponsePaginated<(Point & PointWithBuilding)[]>>(
      `analytics/companies/${companyId}/points`,
      {
        signal,
        params,
      },
    )
    .then((resp) => ({
      points: resp.data.data,
      pagination: resp.data.meta.pagination,
    }));
}

async function fetchFilters(
  { companyId, ...params }: PointFiltersParams,
  signal?: AbortSignal,
) {
  const res = await assetsApiRequest.get<
    ApiResponsePaginated<Record<PointParam, unknown[]>>
  >(`analytics/companies/${companyId}/points/filters`, {
    params,
    signal,
  });
  return res.data.data;
}

export async function fetchPoint(
  params: PointDetailsParams,
  signal?: AbortSignal,
) {
  return assetsApiRequest
    .get<ApiResponse<PointResponse>>(
      `analytics/companies/${params.companyId}/devices/${params.deviceId}/points/${params.id}`,
      {
        signal,
      },
    )
    .then((resp) => PointTypeFactory.create(resp.data.data));
}

export async function fetchGFXPoints(
  { companyId, ...params }: PointGFXQueryParams,
  signal?: AbortSignal,
) {
  return (
    assetsApiRequest
      // NOTE: We are using a POST query here since the externalPoints array can be so big that sending it
      // in GET query params might end up making the query string too large and thus causing an error
      .post<ApiResponsePaginated<PointResponse[]>>(
        `analytics/companies/${companyId}/points/gfxpoints`,
        params,
        { signal },
      )
      .then((resp) => ({
        points: resp.data.data.map((d) => new Point(d)),
        pagination: resp.data.meta.pagination,
      }))
  );
}

async function fetchCalendarPointFeed(
  params: PointDetailsParams,
  signal?: AbortSignal,
): Promise<string> {
  return assetsApiRequest
    .get<string>(
      `analytics/companies/${params.companyId}/calendar/${params.id}/feed`,
      { signal },
    )
    .then((response) => {
      const url = new Blob([response.data], { type: "text/calendar" });
      return URL.createObjectURL(url);
    });
}
